import React from 'react';
import classNames from 'classnames';
import {IntlShape} from 'react-intl';
import moment from 'moment';
import {Lx} from '../../../../i18n/consts';
import {TimeReportItem} from '../../../../features/time-report/types';
import { getTimeReportItemTypeLabel } from '../../helpers/filter-time-report-items';
import styles from './time-report-mobile-card.module.scss';
import {Tooltip} from '@mui/material';

type MetricTone = 'approved' | 'billable' | 'regular';

type Metric = {
    key: string;
    label: string;
    value: number | string;
    tooltip?: string;
    tone?: MetricTone;
};

type Props = {
    item: TimeReportItem;
    intl: IntlShape;
    className?: string;
    detailedByDay?: boolean;
    isProjectView?: boolean;
};

const TimeReportMobileCard: React.FC<Props> = ({ item, intl, className, detailedByDay = false, isProjectView = false }) => {
    const headerTitle = item.clientName;
    const headerSubtitle = isProjectView
        ? (item.employeeName?.trim()?.length ? item.employeeName : '-')
        : (item.projectName?.trim()?.length ? item.projectName : '-');
    const date = moment(item.date, 'YYYY-MM-DD', true);
    const typeLabel = getTimeReportItemTypeLabel(item);
    const formattedDate = date.isValid()
        ? (
            <span className={classNames(styles.title, styles.dateValue)}>
                <span>{date.format('L')}</span>
                <span className={styles.dateWeekday}>{date.format('dd')}</span>
            </span>
        )
        : <span className={styles.title}>{item.date}</span>;

    const showPlanned = isProjectView && !detailedByDay;
    const metrics: Metric[] = [
        ...(showPlanned ? [{
            key: 'planned',
            label: `${intl.formatMessage({ id: Lx.Schedule.PLANNED_HOURS_ABBREVIATION, defaultMessage: 'PL' })}:`,
            value: item.plannedHours ?? 0,
            tooltip: intl.formatMessage({id: Lx.Schedule.PLANNED_HOURS}),
            tone: 'regular' as MetricTone
        }] : []),
        {
            key: 'worked',
            label: `${intl.formatMessage({ id: Lx.Schedule.WORKED_HOURS_ABBREVIATION, defaultMessage: 'WH' })}:`,
            value: item.workHours ?? 0,
            tooltip: intl.formatMessage({id: Lx.Schedule.WORKED_HOURS}),
            tone: 'regular'
        },
        {
            key: 'overtime',
            label: `${intl.formatMessage({ id: Lx.Schedule.OVERTIME_HOURS_ABBREVIATION, defaultMessage: 'OV' })}:`,
            value: item.overtimes ?? 0,
            tooltip: intl.formatMessage({id: Lx.Schedule.OVERTIME_HOURS}),
            tone: 'regular'
        },
        {
            key: 'absence',
            label: `${intl.formatMessage({ id: Lx.Schedule.ABSENCE_HOURS_ABBREVIATION, defaultMessage: 'AH' })}:`,
            value: item.absenceHours ?? 0,
            tooltip: intl.formatMessage({id: Lx.Schedule.ABSENCE_HOURS})
        },
        {
            key: 'timeBank',
            label: `${intl.formatMessage({ id: Lx.Schedule.TIME_BANK_BALANCE_ABBREVIATION, defaultMessage: 'TB' })}:`,
            value: item.timeBankHours ?? 0,
            tooltip: intl.formatMessage({ id: Lx.Schedule.TIME_BANK_BALANCE, defaultMessage: 'Time bank balance' }),
            tone: 'regular'
        },
        {
            key: 'approved',
            label: `${intl.formatMessage({ id: Lx.Schedule.APPROVED_HOURS, defaultMessage: 'Approved' })}:`,
            value: item.approvedHours ?? 0,
            tone: 'approved'
        },
        {
            key: 'billable',
            label: `${intl.formatMessage({ id: Lx.Schedule.BILLABLE_HOURS, defaultMessage: 'Billable' })}:`,
            value: item.billedHours ?? 0,
            tone: 'billable'
        }
    ];

    return (
        <div className={classNames(styles.card, className)}>
            <div className={styles.header}>
                {detailedByDay ? (
                    <>
                        {formattedDate}
                        <span className={styles.subtitle}>{headerSubtitle}</span>
                        <span className={styles.subtitle}>{typeLabel}</span>
                    </>
                ) : (
                    <>
                        <span className={styles.title}>{headerTitle}</span>
                        <span className={styles.subtitle}>{headerSubtitle}</span>
                    </>
                )}
            </div>
            <div className={styles.divider} />
            <div className={styles.metricsRow} style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}>
                {metrics.map((metric) => (
                    <div key={metric.key} className={styles.metric}>
                        {metric.tooltip ? (
                            <Tooltip title={metric.tooltip}>
                                <span className={styles.metricLabel}>{metric.label}</span>
                            </Tooltip>
                        ) : (
                            <span className={styles.metricLabel}>{metric.label}</span>
                        )}
                        <span className={classNames(styles.metricValue, {
                            [styles.metricValueRegular]: metric.tone === 'regular',
                            [styles.metricValueApproved]: metric.tone === 'approved',
                            [styles.metricValueBillable]: metric.tone === 'billable'
                        })}>
                            {metric.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TimeReportMobileCard;
